import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Boxes,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { DeepLinkMissing } from "@/components/DeepLinkMissing";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, Select } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/features/auth/useAuth";
import { MaterialEditDialog } from "@/features/materials/MaterialEditDialog";
import { MaterialsCsvActions } from "@/features/materials/MaterialsCsvActions";
import { CategoryIndex } from "@/lib/category-ref";
import { deleteAttributeValues } from "@/features/materials/attribute-values-api";
import { deleteMaterial } from "@/features/materials/materials-api";
import {
  useCategories,
  useMaterials,
  useUnits,
} from "@/features/materials/useMaterials";
import { usePrices } from "@/features/prices/usePrices";
import { describeWriteError } from "@/features/users/permissions";
import {
  COST_TYPES,
  COST_TYPE_LABELS,
  CURRENCY_SYMBOLS,
} from "@/lib/constants";
import type { CostType, Material } from "@/lib/types";
import { usePagination } from "@/lib/use-pagination";
import { useDeepLink } from "@/lib/use-deep-link";
import { formatDateTime } from "@/lib/utils";

type VisibilityFilter = "all" | "shared" | "private";

/**
 * Stage 4: the shared catalog of cost items.
 *
 * "Cost item" rather than "material": a row here can be a material, labour, equipment or logistics
 * (`costType`), and the estimate engine treats them all the same way.
 */
export function MaterialsPage() {
  const { user, profile } = useAuth();
  const { materials, isLoading, error } = useMaterials(user?.uid);
  const { categories, error: categoriesError } = useCategories();
  const { units } = useUnits();
  const { prices, error: pricesError } = usePrices(user?.uid);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [costType, setCostType] = useState<CostType | "">("");
  const [visibility, setVisibility] = useState<VisibilityFilter>("all");
  const [editing, setEditing] = useState<Material | null>(null);
  const [isCreating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Material | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  // `/materials?edit=<id>` (links from "Несоответствия") opens that item's editor.
  const deepLink = useDeepLink("edit", materials, !isLoading, setEditing);

  const actor = {
    uid: user?.uid ?? "",
    label: profile?.email ?? user?.email ?? user?.uid ?? "",
  };

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // Every item is shown, filtered and searched under its category's CURRENT name: the link is
    // `categoryId`, the stored name is a copy a rename leaves behind (old items resolve by name).
    const index = new CategoryIndex(categories);
    return materials
      .map((item) => {
        const shown = index.displayName(item.categoryId, item.category);
        return shown === item.category ? item : { ...item, category: shown };
      })
      .filter((item) => (category === "" ? true : item.category === category))
      .filter((item) => (costType === "" ? true : item.costType === costType))
      .filter((item) =>
        visibility === "all"
          ? true
          : visibility === "shared"
            ? !item.isPrivate
            : item.isPrivate,
      )
      .filter((item) =>
        needle.length === 0
          ? true
          : `${item.name} ${item.code} ${item.category}`
              .toLowerCase()
              .includes(needle),
      );
  }, [materials, categories, search, category, costType, visibility]);

  // The catalog grows without bound, so the table is paged in the browser: everything is already
  // loaded and filtered here, so this costs no extra reads.
  const pager = usePagination(visible);

  const sharedCount = materials.filter((item) => !item.isPrivate).length;

  // Prices live in their own collection but belong to the material, so they are shown in this table
  // and edited in the material dialog — there is no separate "prices" screen, same as in the app.
  const priceOf = (material: Material) => {
    const price = prices.get(material.id);
    if (!price) return "—";
    return `${price.amount.toLocaleString("ru-RU")} ${CURRENCY_SYMBOLS[price.currency]}`;
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setWriteError(null);
    try {
      await deleteMaterial(deleting.id);
      // No orphan attribute values are left behind (the mobile app cleans them on category change).
      await deleteAttributeValues(deleting.id);
      setDeleting(null);
    } catch (caught) {
      setWriteError(describeWriteError(caught));
    }
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Пункты затрат</h1>
          <p className="text-muted-foreground text-sm">
            {isLoading
              ? "Загрузка…"
              : `Видно: ${materials.length} · в общем каталоге: ${sharedCount}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MaterialsCsvActions materials={materials} categories={categories} actor={actor} />
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Добавить
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              pager.resetPage();
            }}
            placeholder="Поиск по названию, коду или категории"
            className="pl-9"
            aria-label="Поиск пунктов затрат"
          />
        </div>
        <Select
          value={costType}
          onChange={(event) => {
            setCostType(event.target.value as CostType | "");
            pager.resetPage();
          }}
          className="w-44"
          aria-label="Фильтр по типу затрат"
        >
          <option value="">Все типы затрат</option>
          {COST_TYPES.map((type) => (
            <option key={type} value={type}>
              {COST_TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
        <Select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
            pager.resetPage();
          }}
          className="w-52"
          aria-label="Фильтр по категории"
        >
          <option value="">Все категории</option>
          {categories
            .filter((item) =>
              costType === "" ? true : item.costType === costType,
            )
            .map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          {/* Keep a selected category visible even if the cost-type filter would hide it. */}
          {category &&
          !categories.some(
            (item) =>
              item.name === category &&
              (costType === "" || item.costType === costType),
          ) ? (
            <option value={category}>{category}</option>
          ) : null}
        </Select>
        <Select
          value={visibility}
          onChange={(event) => {
            setVisibility(event.target.value as VisibilityFilter);
            pager.resetPage();
          }}
          className="w-44"
          aria-label="Фильтр по видимости"
        >
          <option value="all">Все</option>
          <option value="shared">Общий каталог</option>
          <option value="private">Личные</option>
        </Select>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Каталог не загружен</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {categoriesError ? (
        <Alert variant="warning">
          <AlertTriangle />
          <AlertTitle>Категории не загружены</AlertTitle>
          <AlertDescription>{categoriesError}</AlertDescription>
        </Alert>
      ) : null}

      {pricesError ? (
        <Alert variant="warning">
          <AlertTriangle />
          <AlertTitle>Цены не загружены</AlertTitle>
          <AlertDescription>{pricesError}</AlertDescription>
        </Alert>
      ) : null}

      <DeepLinkMissing
        missingId={deepLink.missingId}
        what="Пункт затрат"
        onDismiss={deepLink.dismiss}
      />

      {writeError ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Действие не выполнено</AlertTitle>
          <AlertDescription>{writeError}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="py-0">
        <CardContent className="px-0">
          {isLoading ? (
            <div className="flex flex-col gap-3 p-6">
              {[0, 1, 2, 3, 4].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-16 text-sm">
              <Boxes className="size-8" />
              {materials.length === 0 ? "Каталог пуст." : "Ничего не найдено."}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    <TableHead>Категория</TableHead>
                    <TableHead>Ед.</TableHead>
                    <TableHead>Цена</TableHead>
                    <TableHead>Тип</TableHead>
                    <TableHead>Видимость</TableHead>
                    <TableHead>Изменён</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pager.pageItems.map((item) => (
                    <TableRow
                      key={item.id}
                      className={item.isActive ? undefined : "opacity-60"}
                    >
                      <TableCell>
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate font-medium">
                            {item.name}
                          </span>
                          <span className="text-muted-foreground truncate text-xs">
                            {item.code || item.id}
                            {item.isActive ? "" : " · скрыт"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.category || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.unit || "—"}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {priceOf(item)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {COST_TYPE_LABELS[item.costType]}
                      </TableCell>
                      <TableCell>
                        {item.isPrivate ? (
                          <Badge variant="secondary" className="gap-1">
                            <Lock className="size-3" />
                            личный
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1">
                            <Users className="size-3" />
                            общий
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                        {formatDateTime(item.updatedAt)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(item)}
                        >
                          <Pencil />
                          Изменить
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleting(item)}
                        >
                          <Trash2 />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={pager.page}
                pageCount={pager.pageCount}
                pageSize={pager.pageSize}
                total={pager.total}
                firstShown={pager.firstShown}
                lastShown={pager.lastShown}
                onPage={pager.setPage}
                onPageSize={pager.setPageSize}
                itemsLabel="пунктов"
              />
            </>
          )}
        </CardContent>
      </Card>

      <p className="text-muted-foreground text-xs">
        Пункт затрат — это материал, работа, оборудование или логистика: в
        расчёте они устроены одинаково, различает их только тип затрат. Каталог
        читается двумя запросами — свои пункты и общие — и объединяется в
        браузере: правила безопасности не разрешают один общий запрос.
        Публиковать пункт (видимость «общий каталог») может только
        администратор. Цена задаётся здесь же, в карточке: у общего пункта она
        общая, у личного — личная; пустое поле означает, что цены нет.
        CSV-формат совпадает с мобильным приложением: строки сопоставляются по
        коду, поэтому повторный импорт обновляет записи, а не создаёт копии.
        Категории и их атрибуты — в разделе «Категории». Постраничный показ
        работает поверх уже загруженного списка, поэтому фильтры и поиск считают
        по всему каталогу, а не по текущей странице.
      </p>

      {isCreating ? (
        <MaterialEditDialog
          material={null}
          categories={categories}
          units={units}
          actor={actor}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {editing ? (
        <MaterialEditDialog
          material={editing}
          categories={categories}
          units={units}
          actor={actor}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {deleting ? (
        <Dialog open onOpenChange={(open) => !open && setDeleting(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Удалить пункт затрат?</DialogTitle>
              <DialogDescription>
                «{deleting.name}» будет удалён безвозвратно. Если он
                используется в сметах, строки останутся, но ссылка на пункт
                перестанет открываться.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)}>
                Отмена
              </Button>
              <Button variant="destructive" onClick={() => void handleDelete()}>
                <Trash2 />
                Удалить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
